<template>
	<div>
		<!-- add edit dialog -->
		<v-dialog v-model="addEditDialog" max-width="600">
			<v-card>
				<v-card-title class="primary--text">
					<v-btn icon @click="addEditDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>

					<v-spacer/>
					<span class="me-8">
						{{ !passengerId ? $t('PassengersData.add-passenger') : $t('PassengersData.edit-passenger')}}
					</span>
					<v-spacer/>
				</v-card-title>
				<v-card-text class="pb-0">
					<v-row>
						<!-- name -->
						<v-col md="6" cols="12">
							<v-text-field
								v-model="addEditDialogData.name"
								:label="$t('inputs.name')"
								dense
								hide-details
								outlined
							></v-text-field>
						</v-col>

						<!-- grade -->
						<v-col md="6" cols="12">
							<v-text-field
								v-model="addEditDialogData.gradeId"
								:label="$t('inputs.grade')"
								dense
								hide-details
								outlined
							></v-text-field>
						</v-col>

						<!-- phone -->
						<v-col md="6" cols="12">
							<v-text-field
								v-model="addEditDialogData.phone"
								:label="$t('inputs.phone')"
								dense
								hide-details
								outlined
							></v-text-field>
						</v-col>

						<!-- vehicle -->
						<v-col md="6" cols="12">
							<v-autocomplete
								v-model="addEditDialogData.vehicleId"
								:label="$t('inputs.vehicle-no')"
								:items="vehicles"
								item-text="BusNo"
								item-value="Id"
								dense
								hide-details
								outlined
							></v-autocomplete>
						</v-col>
						
						<!-- arrive time -->
						<v-col md="6" cols="12">
							<v-menu
								ref="arriveTimeMenu"
								v-model="arriveTimeMenu"
								:close-on-content-click="false"
								:return-value.sync="addEditDialogData.arriveTime"
								transition="scale-transition"
								offset-y
								max-width="250"
								min-width="250"
							>
								<template v-slot:activator="{ on, attrs }">
									<v-text-field
										:value="addEditDialogData.arriveTime"
										:label="$t('inputs.vehicle-arrive-time')"
										prepend-inner-icon="mdi-clock-time-four-outline"
										readonly
										outlined
										dense
										persistent-hint
										hide-details="auto"
										:disabled="loading"
										v-bind="attrs"
										v-on="on"
									></v-text-field>
								</template>
								<v-time-picker
									v-if="arriveTimeMenu"
									v-model="addEditDialogData.arriveTime"
									scrollable
									full-width
									@click:minute="$refs.arriveTimeMenu.save(addEditDialogData.arriveTime)"
								></v-time-picker>
							</v-menu>
						</v-col>

						<!-- address -->
						<v-col md="6" cols="12">
							<v-text-field
								v-model="addEditDialogData.address"
								:label="$t('inputs.address')"
								dense
								hide-details
								outlined
							></v-text-field>
						</v-col>
						
						<!-- username -->
						<v-col md="6" cols="12">
							<v-text-field
								v-model="addEditDialogData.username"
								:label="$t('inputs.username')"
								dense
								hide-details
								outlined
							></v-text-field>
						</v-col>
						
						<!-- password -->
						<v-col md="6" cols="12">
							<v-text-field
								v-model="addEditDialogData.password"
								:label="$t('inputs.password')"
								type="password"
								dense
								hide-details
								outlined
							></v-text-field>
						</v-col>
					</v-row>
				</v-card-text>
				<v-card-actions>
					<v-spacer/>
					<v-btn
						color="green white--text"
						:loading="submitLoading"
						@click="''"
					>
						{{$t('save')}}
					</v-btn>
					<v-btn @click="addEditDialog = false">
						{{$t('cancel')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- location dialog -->
		<v-dialog
			v-model="locationDialog"
			fullscreen
			persistent
			width="900"
		>
			<v-card>
				<v-card-title class="primary--text">
					<v-btn icon @click="locationDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>

					<v-spacer/>
					{{$t('PassengersData.set-home-location')}}
					({{ currentPassengerName }})
					<v-spacer/>
				</v-card-title>

				<v-card-text class="pb-0">
					<v-form @submit.prevent="goToCoordinates">
						<!-- search -->
						<v-row class="align-center ma-0">
							<!-- coordinates -->
							<v-col lg="3" md="4" sm="6" cols="12">
								<v-text-field
									v-model="searchCoordinates"
									:label="$t('inputs.coordinates')"
									outlined
									dense
									hide-details
								></v-text-field>
							</v-col>
							
							<!-- search -->
							<v-col cols="auto">
								<v-btn
									:disabled="!Boolean(searchCoordinates)"
									color="orange white--text"
									type="submit"
								>
									{{$t('search')}}
								</v-btn>
							</v-col>
						</v-row>
					</v-form>

					<!-- map -->
					<div :style="{ height: `${$vuetify.breakpoint.height - 180}px` }">
						<v-map
							v-if="showMap"
							ref="map"
							:points.sync="points"
							:context-menu-items="contextMenuItems"
							:loading="loading"
							@context-menu-select="setMarker"
						/>
					</div>
				</v-card-text>

				<v-card-actions class="justify-end">
					<v-btn
						color="green white--text"
						:loading="submitLoading"
						@click="saveHomeLocation"
					>
						{{$t('save')}}
					</v-btn>
					<v-btn @click="locationDialog = false">
						{{$t('cancel')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- qr code dialog -->
		<v-dialog v-model="qrCodeDialog" width="300">
			<v-card>
				<v-card-title class="primary--text">
					<v-btn icon @click="qrCodeDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>

					<v-spacer/>
					<span class="me-10">{{$t('PassengersData.qr-code')}}</span>
					<v-spacer/>
				</v-card-title>

				<v-card-text class="text-center pb-2">
					<v-progress-circular
						v-if="qrCodeLoading"
						color="primary"
						size="35"
						width="2"
						indeterminate
						class="qr-loader"
					/>

					<template v-else>
						<canvas ref="qrCodeArea"></canvas>
						<p class="text-body-1 primary--text mt-n2 mb-0">{{ currentPassengerName }}</p>
					</template>
				</v-card-text>

				<v-card-actions class="justify-end">
					<v-btn
						color="green white--text"
						:disabled="qrCodeLoading"
						@click="print"
					>
						{{$t('print')}}
					</v-btn>
					<v-btn @click="qrCodeDialog = false">
						{{$t('cancel')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- transfer student -->
		<v-dialog v-model="transferDialog" max-width="700">
			<v-card>
				<v-card-title class="primary--text">
					<v-btn icon @click="transferDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>

					<v-spacer/>
					<span class="me-8">
						{{ $t('PassengersData.student-temporarily-transferred-from-bus-to-bus') }}
					</span>
					<v-spacer/>
				</v-card-title>
					<v-form ref="form" @submit.prevent="moveStudentToVehicle">
						<v-card-text class="pb-0">
							<v-row>
								<!-- name -->
								<v-col md="6" cols="12">
									<v-text-field
										v-model="transferDialogData.name"
										:label="$t('inputs.name')"
										dense
										hide-details
										outlined
										:disabled="submitLoading || loadingGetMove"
									></v-text-field>
								</v-col>

								<!-- vehicle -->
								<v-col md="6" cols="12">
									<v-autocomplete
										v-model="transferDialogData.vehicleId"
										:label="$t('inputs.vehicle-no')"
										:items="vehicles"
										item-text="BusNo"
										item-value="Id"
										dense
										hide-details
										outlined
										:disabled="submitLoading || loadingGetMove"
									></v-autocomplete>
								</v-col>

								<!-- start date -->
								<v-col md="6" cols="12">
									<v-menu
										ref="startDateDialogMenu"
										v-model="startDateDialogMenu"
										:close-on-content-click="false"
										:return-value.sync="transferDialogData.startDate"
										transition="scale-transition"
										offset-y
										max-width="250"
										min-width="250"
									>
										<template v-slot:activator="{ on, attrs }">
											<v-text-field
												:value="transferDialogData.startDate"
												:label="$t('inputs.start-date')"
												prepend-inner-icon="mdi-calendar-range"
												readonly
												outlined
												dense
												persistent-hint
												hide-details="auto"
												:disabled="loading || submitLoading || loadingGetMove"
												v-bind="attrs"
												v-on="on"
											></v-text-field>
										</template>
										<v-date-picker
											v-if="startDateDialogMenu"
											v-model="transferDialogData.startDate"
											scrollable
											full-width
											@click:date="$refs.startDateDialogMenu.save(transferDialogData.startDate)"
										></v-date-picker>
									</v-menu>
								</v-col>

								<!-- end date -->
								<v-col md="6" cols="12">
									<v-menu
										ref="endDateDialogMenu"
										v-model="endDateDialogMenu"
										:close-on-content-click="false"
										:return-value.sync="transferDialogData.endDate"
										transition="scale-transition"
										offset-y
										max-width="250"
										min-width="250"
									>
										<template v-slot:activator="{ on, attrs }">
											<v-text-field
												:value="transferDialogData.endDate"
												:label="$t('inputs.end-date')"
												prepend-inner-icon="mdi-calendar-range"
												readonly
												outlined
												dense
												persistent-hint
												hide-details="auto"
												:disabled="loading || submitLoading || loadingGetMove"
												v-bind="attrs"
												v-on="on"
											></v-text-field>
										</template>
										<v-date-picker
											v-if="endDateDialogMenu"
											v-model="transferDialogData.endDate"
											scrollable
											full-width
											@click:date="$refs.endDateDialogMenu.save(transferDialogData.endDate)"
										></v-date-picker>
									</v-menu>
								</v-col>

								<!-- trip type -->
								<v-col md="6" cols="12">
									<v-autocomplete
										v-model="transferDialogData.tripType"
										:label="$t('inputs.trip-type')"
										:items="tripTypeArray"
										item-text="label"
										item-value="value"
										dense
										hide-details
										outlined
										:disabled="submitLoading || loadingGetMove"
									/>
								</v-col>
								<!-- arrive time -->
								<v-col md="6" cols="12">
									<v-menu
										ref="arriveTimeTransferDialogMenu"
										v-model="arriveTimeTransferDialogMenu"
										:close-on-content-click="false"
										:return-value.sync="transferDialogData.arriveTime"
										transition="scale-transition"
										offset-y
										max-width="250"
										min-width="250"
									>
										<template v-slot:activator="{ on, attrs }">
											<v-text-field
												:value="transferDialogData.arriveTime"
												:label="$t('inputs.vehicle-arrive-time')"
												prepend-inner-icon="mdi-clock-time-four-outline"
												readonly
												outlined
												dense
												persistent-hint
												hide-details="auto"
												:disabled="loading || submitLoading || loadingGetMove"
												v-bind="attrs"
												v-on="on"
											></v-text-field>
										</template>
										<v-time-picker
											v-if="arriveTimeTransferDialogMenu"
											v-model="transferDialogData.arriveTime"
											scrollable
											full-width
											@click:minute="$refs.arriveTimeTransferDialogMenu.save(transferDialogData.arriveTime)"
										></v-time-picker>
									</v-menu>
								</v-col>
								<v-col cols="12">
									<v-data-table
										:items="items"
										:headers="headersTransfer"
										disable-sort
										:items-per-page="-1"
										fixed-header
										height="200"
										dense
										class="elevation-2"
										hide-default-footer
										:loading="loadingGetMove"
									>
										<template v-slot:item.BusId="{ item }">
											<td>{{ getVehiclesByBusId(item.BusId)?.BusNo }}</td>
										</template>

										<template v-slot:item.FromDate="{ item }">
											<td>{{ moment(item.FromDate).format('YYYY-MM-DD') }}</td>
										</template>

										<template v-slot:item.ToDate="{ item }">
											<td>{{ moment(item.ToDate).format('YYYY-MM-DD') }}</td>
										</template>

										<template v-slot:item.TimeArrival="{ item }">
											<td> {{ moment.utc(item.TimeArrival)
											.format('hh:mm a')
											.replace(/am|pm/, match => ({ am: 'ص', pm: 'م' })[match]) }}</td>
										</template>

										<template v-slot:item.TripType="{ item }">
											<td>{{ item.TripType ===  tripType.roundTrip ? $t('trip-type.round-trip') : item.TripType === tripType.oneWay ? $t('trip-type.one-way') : $t('trip-type.return-only') }}</td>
										</template>
									</v-data-table>
								</v-col>
							</v-row>
						</v-card-text>
						<v-card-actions>
							<v-spacer/>
							<v-btn
								color="green white--text"
								:loading="submitLoading"
								type="submit"
							>
								{{$t('save')}}
							</v-btn>
							<v-btn @click="transferDialog = false" :disabled="submitLoading">
								{{$t('cancel')}}
							</v-btn>
						</v-card-actions>
					</v-form>
			</v-card>
		</v-dialog>
		
		<!-- filter -->
		<v-form @submit.prevent="filter" :disabled="loading">
			<v-row class="align-center mx-0 mb-0">
				<!-- name -->
				<v-col v-if="false" lg="2" md="3" sm="6" cols="12" class="px-1">
					<v-text-field
						v-model="filterData.name"
						:label="$t('inputs.name')"
						dense
						hide-details
						outlined
					></v-text-field>
				</v-col>
				
				<!-- vehicle no -->
				<v-col lg="1" md="2" sm="4" cols="12" class="px-1">
					<v-autocomplete
						v-model="filterData.vehicleId"
						:label="$t('inputs.vehicle-no')"
						:items="vehicles"
						item-text="BusNo"
						item-value="Id"
						dense
						append-icon
						hide-details
						outlined
					></v-autocomplete>
				</v-col>
				
				<!-- education year -->
				<v-col lg="2" md="3" sm="4" cols="12" class="px-1">
					<v-autocomplete
						v-model="filterData.educationYear"
						:label="$t('inputs.education-year')"
						:items="educationYears"
						dense
						append-icon
						hide-details
						outlined
					></v-autocomplete>
				</v-col>
				
				<!-- grade -->
				<v-col lg="1" md="2" sm="4" cols="12" class="px-1">
					<v-autocomplete
						v-model.number="filterData.gradeId"
						:label="$t('inputs.grade')"
						:items="grades"
						item-text="SpecialtyName"
						item-value="SpecialtyId"
						dense
						append-icon
						hide-details
						outlined
						@change="!filterData.gradeId ? filterData.classId = null : null"
					></v-autocomplete>
				</v-col>

				<!-- class -->
				<v-col v-if="filterData.gradeId" lg="1" md="2" sm="4" cols="12" class="px-1">
					<v-autocomplete
						v-model="filterData.classId"
						:label="$t('inputs.class')"
						:items="getGradeById(filterData.gradeId).Courses"
						item-text="CourseName"
						item-value="Id"
						dense
						append-icon
						hide-details
						outlined
					></v-autocomplete>
				</v-col>
				
				<!-- search -->
				<v-col cols="auto" class="px-1">
					<v-btn
						color="orange white--text"
						type="submit"
						:disabled="filterData.gradeId && !filterData.classId"
						:loading="loading"
					>
						{{$t('search')}}
					</v-btn>
				</v-col>

				<v-spacer/>

				<!-- add vehicle -->
				<v-col v-if="userAddEditMode" cols="auto" class="px-1">
					<v-btn
						color="green white--text"
						class="px-3"
						@click="addEditDialog = true"
					>
						<v-icon class="me-1">mdi-plus-circle-outline</v-icon>
						{{$t('add')}}
					</v-btn>
				</v-col>

				<!-- qr-code generator -->
				<v-col cols="auto" class="px-1">
					<v-tooltip
						bottom
						nudge-bottom="-8"
						:disabled="Boolean(filterData.vehicleId)"
					>
						<template v-slot:activator="{ on, attrs }">
							<v-btn
								:color="
									filterData.vehicleId
										? 'light-blue white--text'
										: 'grey lighten-2 grey--text'
								"
								:depressed="!filterData.vehicleId"
								:ripple="Boolean(filterData.vehicleId)"
								:loading="generateQRAllLoading"
								@click="filterData.vehicleId ? generateQRAll() : null"
								v-bind="attrs"
								v-on="on"
							>
								<v-icon size="22" class="me-1">mdi-qrcode</v-icon>
								{{$t('PassengersData.generate-qr-all')}}
							</v-btn>
						</template>
						يجب اختيار عربة
					</v-tooltip>
				</v-col>
			</v-row>
		</v-form>

		<!-- table -->
		<v-data-table
			:headers="headers"
			:items="!loading ? passengers : []"
			:loading="loading"
			:options.sync="options"
			:server-items-length="totalItems"
			disable-sort
			:items-per-page="20"
			:footer-props="{
				'items-per-page-options': [10, 20, 40, 100]
			}"
			fixed-header
			:height="$vuetify.breakpoint.height - 195"
			dense
			class="elevation-2"
		>
			<!-- name -->
			<template v-slot:item.FirstName="{ item }">
				{{ `${item.FirstName} ${item.FatherName || ''} ${item.LastName }` }}
			</template>

			<!-- arrive time -->
			<template v-slot:item.ArrivalTime="{ item }">
				{{
					moment.utc(item.ArrivalTime)
						.format('hh:mm a')
						.replace(/am|pm/, match => ({ am: 'ص', pm: 'م' })[match])
				}}
			</template>

			<!-- actions -->
			<template v-slot:item.actions="{ item }">
				<!-- edit -->
				<v-tooltip v-if="userAddEditMode" top>
					<template v-slot:activator="{ on, attrs }">
						<v-btn
							depressed
							height="30"
							width="30"
							min-width="30"
							class="my-1 me-2"
							color="purple lighten-4 primary--text"
							v-on="on"
							v-bind="attrs"
							@click="passengerId = item.AccountId; addEditDialog = true;"
						>
							<v-icon>mdi-pencil</v-icon>
						</v-btn>
					</template>

					{{$t('edit')}}
				</v-tooltip>

				<!--  -->
				<v-tooltip top>
					<template v-slot:activator="{ on, attrs }">
						<v-btn
							depressed
							height="30"
							width="30"
							min-width="30"
							class="my-1 me-2"
							color="purple lighten-4 primary--text"
							v-on="on"
							v-bind="attrs"
							@click="passenger = item; transferDialog = true;"
						>
							<v-icon>mdi-train-car</v-icon>
						</v-btn>
					</template>

					{{$t('student-transfer')}}
				</v-tooltip>

				<!-- location -->
				<v-tooltip top>
					<template v-slot:activator="{ on, attrs }">
						<v-btn
							depressed
							height="30"
							width="30"
							min-width="30"
							class="my-1"
							color="blue lighten-4 primary--text"
							v-on="on"
							v-bind="attrs"
							@click="passengerId = item.AccountId; locationDialog = true;"
						>
							<v-icon>mdi-home-map-marker</v-icon>
						</v-btn>
					</template>

					{{$t('tooltips.update-home-location')}}
				</v-tooltip>

				<!-- qrcode -->
				<v-tooltip top>
					<template v-slot:activator="{ on, attrs }">
						<v-btn
							depressed
							height="30"
							width="30"
							min-width="30"
							class="ms-2 my-1"
							color="orange lighten-4 primary--text"
							v-on="on"
							v-bind="attrs"
							@click="passengerId = item.AccountId; qrCodeDialog = true;"
						>
							<v-icon>mdi-qrcode</v-icon>
						</v-btn>
					</template>

					{{$t('tooltips.generate-qr')}}
				</v-tooltip>
				
				<!-- show movements -->
				<v-tooltip top>
					<template v-slot:activator="{ on, attrs }">
						<v-btn
							depressed
							height="30"
							width="30"
							min-width="30"
							class="ms-2 my-1"
							color="green lighten-4 primary--text"
							v-on="on"
							v-bind="attrs"
							:to="{ name: 'latest-movements', params: { id: item.AccountId } }"
						>
							<v-icon>mdi-arrow-decision-outline</v-icon>
						</v-btn>
					</template>

					{{$t('tooltips.show-movements')}}
				</v-tooltip>
			</template>
		</v-data-table>
	</div>
</template>

<script>
import QRcode from 'qrcode'
import printJS from 'print-js'
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'
import { userAddEditMode } from '@/configs/general.configs'
import { tripTypeArray, tripType } from '@/helpers/enums'
import rules from '@/helpers/validation rules'

export default {
	name: 'PassengersData',

	data: () => ({
		// helpers
		moment,
		
		loading: false,
		generateQRAllLoading: false,
		submitLoading: false,

		passengerId: null,

		arriveTimeMenu: false,
		
		// configs
		userAddEditMode,

		// location dialog
		showMap: false,
		locationDialog: false,
		searchCoordinates: null,

		// add edit dialog
		addEditDialog: false,

		// pagination
		options: {},
		totalItems: null,

		// QR
		qrCodeLoading: false,
		qrCodeDialog: false,
		qrUrl: null,

		// filter
		filterData: {
			name: null,
			vehicleId: null,
			educationYear: null,
			gradeId: null,
			classId: null,
		},

		// submit data
		points: [],
		addEditDialogData: {
			name: null,
			gradeId: null,
			phone: null,
			vehicleId: null,
			arriveTime: null,
			address: null,
			username: null,
			password: null,
		},

		transferDialog: false,
		transferDialogData: {
			name: null,
			vehicleId: null,
			arriveTime: null,
			startDate: null,
			endDate: null,
		},
		arriveTimeTransferDialogMenu: null,
		// enums
		tripTypeArray,

		startDateDialogMenu: null,
		endDateDialogMenu: null,
		passenger: {},

		rules,

		items: [],
		tripType,
		loadingGetMove: false
	}),

	watch: {
		addEditDialog(newVal) {
			if (newVal) {
				// 
			}
		},

		transferDialog (newVal) {
			if (newVal) {
				this.loadingGetMove = true
				this.$store.dispatch('passengers/getMovingPassengerById', { accountId: this.passenger.AccountId }).then(({ data }) => {
					this.items = data
				}).finally(() => {
					this.loadingGetMove = false
				})
				this.transferDialogData.name = this.passenger.FirstName + ' ' + this.passenger.LastName	
				this.transferDialogData.vehicleId = this.getVehiclesByBusNo(this.passenger.BusNo)?.Id
			} else {
				this.transferDialogData.name = null
				this.transferDialogData.vehicleId = null
				this.transferDialogData.arriveTime = null
				this.transferDialogData.startDate = null
				this.transferDialogData.endDate = null
				this.transferDialogData.tripType = null
				this.passenger = {}
				this.items = []
			}
		},

		async locationDialog(newVal) {
			if (newVal) {
				await this.$nextTick();
				this.showMap = true;
				await this.$nextTick();
				// get student home location
				const passenger = this.getPassengerById(this.passengerId);
				let point;
				if (passenger.Lat && passenger.Lng) {
					// go to home location
					point = { lat: passenger.Lat, lng: passenger.Lng };
					this.setMarker(point);
					this.$refs.map.setViewTo(point);
				} else {
					// go to company location
					if (this.companyCoordinates) {
						this.points.push(this.companyCoordinates);
						this.$refs.map.setViewTo(this.companyCoordinates.latLng);
					}
				}
			} else {
				this.showMap = false;
				this.points = [];
				this.searchCoordinates = null;
			}
		},

		options() {
			this.filter();
		},

		qrCodeDialog(newVal) {
			if (newVal) {
				this.qrCodeLoading = true;
				setTimeout(() => {
					this.qrCodeLoading = false;
					this.$nextTick(() => {
						this.generateQR();
					})
				}, 500);
			} else {
				this.qrUrl = null;
			}
		}
	},

	computed: {
		...mapState({
			grades: state => state.grades.grades,
			vehicles: state => state.vehicles.vehicles,
			passengers: state => state.passengers.passengers,
			companyInfo: state => state.companyInfo.companyInfo,
		}),
		...mapGetters({
			getGradeById: 'grades/getGradeById',
			getPassengerById: 'passengers/getPassengerById',
			getVehiclesByBusNo: 'vehicles/getVehiclesByBusNo',
			getVehiclesByBusId: 'vehicles/getVehiclesByBusId'
		}),

		headers() {
			const headers = [
				{
					text: this.$t('headers.name'),
					value: 'FirstName'
				},
				{
					text: this.$t('headers.grade'),
					value: 'SpecialtyName',
					width: 120
				},
				{
					text: this.$t('headers.phone'),
					value: 'PhoneNumber',
					width: 140
				},
				{
					text: this.$t('headers.vehicle-no'),
					value: 'BusNo',
					width: 110
				},
				{
					text: this.$t('headers.vehicle-arrive-time'),
					value: 'ArrivalTime',
					width: 150
				},
				{
					text: this.$t('headers.address'),
					value: 'Address'
				},
				{
					text: '',
					value: 'actions',
					width: 185
				},
			];

			for (let i = 0; i < headers.length; i++) {
				headers[i].class = headers.class ? headers.class + ' pe-0' : 'pe-0';
				headers[i].cellClass = headers.cellClass ? headers.cellClass + ' pe-0' : 'pe-0';
			}

			return headers
		},

		headersTransfer () {
			return [
				{
					text: this.$t('headers.vehicle-no'),
					value: 'BusId'
				},
				{
					text: this.$t('headers.from-date'),
					value: 'FromDate'
				},
				{
					text: this.$t('headers.to-date'),
					value: 'ToDate'
				},
				{
					text: this.$t('headers.vehicle-arrive-time'),
					value: 'TimeArrival'
				},
				{
					text: this.$t('headers.trip-type'),
					value: 'TripType'
				}
			]
		},

		contextMenuItems() {
			return [
				{
					text: this.$t('select'),
					action: 'select',
					icon: 'mdi-map-marker-radius',
					iconColor: 'success darken-2',
					class: 'success--text text--darken-2'
				}
			]
		},

		companyCoordinates() {
			if (this.companyInfo.SchoolLocation.Lat && this.companyInfo.SchoolLocation.Lng) {
				return {
					latLng: {
						lat: this.companyInfo.SchoolLocation.Lat,
						lng: this.companyInfo.SchoolLocation.Lng
					},
					icon: {
						size: [44, 44],
						anchor: [22, 44],
						url: require('@/assets/imgs/star.png')
					},
					popupText: this.$t('company-location'),
					draggable: false
				}
			}
			return null
		},

		currentPassengerName() {
			const passenger = this.getPassengerById(this.passengerId);
			return passenger ? `${passenger.FirstName} ${passenger.FatherName || ''} ${passenger.LastName}` : null;
		},

		educationYears() {
			if (this.companyInfo.AcademicYear) {
				let current = Number(this.companyInfo.AcademicYear.split('-')[0]);
				const years = [];
				years.push(this.companyInfo.AcademicYear);
				for (let i = 0; i < 5; i++) {
					years.push(`${current - 1}-${current}`)
					current--;
				}
				return years
			} else {
				return [];
			}
		},
	},

	methods: {
		filter() {
			this.loading = true;
			const { page, itemsPerPage } = this.options;
			return this.$store.dispatch('passengers/fetchAll', {
				page,
				itemsPerPage,
				vehicleId: this.filterData.vehicleId,
				academicYear: this.filterData.educationYear,
				specialtyId: this.filterData.gradeId,
				courseId: this.filterData.classId
			}).then(data => {
				this.totalItems = data.TotalCount;
			}).finally(() => {
				this.loading = false;
			});
		},

		// location dialog
		setMarker(coordinates) {
			this.points = [];
			this.points.push({
				latLng: coordinates,
				icon: {
					size: [44, 44],
					anchor: [22, 44],
					url: require('@/assets/imgs/home.png')
				},
				draggable: true
			});
			if (this.companyCoordinates) {
				this.points.push(this.companyCoordinates);
			}
		},

		goToCoordinates() {
			const coordinates = this.searchCoordinates.split(',').map(c => Number(c));
			if (isNaN(coordinates[0]) || isNaN(coordinates[1])) return;

			const latLng = { lat: coordinates[0], lng: coordinates[1] };
			this.$refs.map.setViewTo(latLng);
			this.setMarker(latLng);
		},

		saveHomeLocation() {
			if (!this.points.find(c => c.draggable)) {
				this.$eventBus.$emit('show-snackbar', 'warning', this.$t('messages.set-location'));
				return;
			}
			this.submitLoading = true;
			this.$store.dispatch('passengers/updateLocation', {
				passengerId: this.passengerId,
				point: this.points[0].latLng
			}).then(() => {
				this.$eventBus.$emit('show-snackbar', 'info', this.$t('messages.saved-successfully'));
				this.locationDialog = false;
				this.filter();
			}).finally(() => {
				this.submitLoading = false
			})
		},

		// qrcode dialog
		generateQR() {
			const passenger = this.getPassengerById(this.passengerId);

			const opts = {
				errorCorrectionLevel: 'H',
				type: 'image/jpeg',
				quality: 1,
				margin: 1,
				width: 250,
				color: {
					dark: '#163963',
					light: '#fff'
				}
			}
			QRcode.toDataURL(this.$refs.qrCodeArea, passenger.QrData, opts, (error, url) => {
				if (error) throw error;
				this.qrUrl = url;
			})
		},

		print() {
			printJS({
				printable: `
					<div style="display: inline-flex; flex-direction: column; justify-content: center; align-items: center; margin-bottom: 10px;">
						<img src="${this.qrUrl}">
						<span>${this.currentPassengerName}</<span>
					</div>
				`,
				css: '/css/printjs.css',
				type: 'raw-html'
			});
		},

		// qqr code generate all
		generateQRAll() {
			this.generateQRAllLoading = true;
			this.$store.dispatch('passengers/fetchQRCodeAll', {
				vehicleId: this.filterData.vehicleId,
				academicYear: this.filterData.educationYear,
				specialtyId: this.filterData.gradeId,
				courseId: this.filterData.classId
			}).then((data) => {
				const items = data.map(c => ({
					name: `${c.FirstName} ${c.FatherName || ''} ${c.LastName}`,
					qrcode: c.QrData
				}))
				const opts = {
					errorCorrectionLevel: 'H',
					type: 'image/jpeg',
					quality: 1,
					margin: 1,
					width: 231,
					color: {
						dark: '#163963',
						light: '#fff'
					}
				}
				const images = [];
				items.forEach(item => {
					QRcode.toDataURL(item.qrcode, opts, (error, url) => {
						if (error) throw error;
						images.push(`
							<div style="display: inline-flex; flex-direction: column; justify-content: center; align-items: center; margin-bottom: 10px;">
								<img src="${url}">
								<span>${item.name}</<span>
							</div>
						`);
					})
				});
				printJS({
					printable: images.join(''),
					css: '/css/printjs.css',
					type: 'raw-html'
				});
				this.generateQRAllLoading = false;
			})
		},

		moveStudentToVehicle() {
			this.submitLoading = true
			this.$store.dispatch('passengers/moveStudentBus', {
				passengerId: this.passengerId,
				vehicleId: this.transferDialogData.vehicleId,
				arriveTime: this.transferDialogData.arriveTime,
				startDate: this.transferDialogData.startDate,
				endDate: this.transferDialogData.endDate,
				info: {

					AccountId: this.passenger.AccountId,
					CurrentBusId: this.getVehiclesByBusNo(this.passenger.BusNo)?.Id,
					NewBusId: this.transferDialogData.vehicleId,
					// Area: this.transferDialogData.area,
					FromDate: this.transferDialogData.startDate,
					ToDate: this.transferDialogData.endDate,
					Reason: this.transferDialogData.reason

				}
			}).then(() => {
				this.$eventBus.$emit('show-snackbar', 'info', this.$t('messages.saved-successfully'));
				this.transferDialog = false;
				this.filter();
			}).finally(() => {
				this.passenger = {}
				this.submitLoading = false
			})
		},
	},

	mounted() {
		this.loading = true;
		Promise.all([
			this.filter(),
			!this.vehicles.length ? this.$store.dispatch('vehicles/fetchAll') : null,
			!this.grades.length ? this.$store.dispatch('grades/fetchAll') : null,
		]).finally(() => {
			this.loading = false;
		})
	}
}
</script>

<style lang="scss" scoped>
.qr-loader {
	margin: 110px auto;
}
</style>